import { createSelector, weakMapMemoize } from 'reselect'
import { Experiments } from 'constants/experiments'
import { queryKeySourceTotal } from 'constants/url'
import { parseAmount } from 'lib/metasearch/ghaPriceGap'
import { getOptimizelyExperimentDecisionVariation, isAllowedToTriggerExperiment } from 'lib/optimizely/optimizelyUtils'
import {
  ghaPriceMatchKey,
  parseGhaPackageRefundable,
  parseGhaRefundableParam,
  type GhaPriceMatchClickThrough,
} from 'lib/metasearch/ghaPriceMatch'
import { isMetasearchPublicRateActive } from 'lib/metasearch/metasearchPublicRate'
import { isB2cMetasearchRateRule, isEligibleMetasearchCugRateRule } from 'lib/metasearch/metasearchRateRules'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import { stringParam } from 'lib/url/queryStringUtils'

const METASEARCH_PAGE_PATH = '/:regionCode/metasearch/:propertyId'

/**
 * Whether this user is in the price-match rollout. `control`, `off` and a missing decision all
 * read false, so an Optimizely outage leaves today's voucher flow exactly as it is.
 */
export function isGhaPriceMatchEnabled(state: App.State): boolean {
  const experimentId = Experiments.ghaPriceMatch.id
  if (!isAllowedToTriggerExperiment(true, state.utm.content, experimentId)) return false
  const decision = getOptimizelyExperimentDecisionVariation(state, experimentId)
  // `true` is the local REACT_APP_OPTIMIZELY_<id> override; control, off and absent all fall through.
  return decision === 'on' || decision === true
}

/**
 * The GHA click-through on the current URL, or undefined when any part of it is missing (Google
 * sometimes renders its macros empty). The metasearch landing page is the only place that reads
 * Google's parameter names; every hop forward carries the gha* names written by useOfferUrl.
 * B2C and CUG clicks are price matched, each on its own svc-bedbank session; a missing or
 * unreadable `ruleId` is neither.
 */
export const getGhaPriceMatch = createSelector(
  (state: App.State) => state.router.location.pathname,
  (state: App.State) => state.router.location.query,
  (pathname, query): GhaPriceMatchClickThrough | undefined => {
    const token = stringParam(query?.gpid)
    const accountType = stringParam(query?.accountType)
    const ruleId = stringParam(query?.ruleId)
    const isCug = isEligibleMetasearchCugRateRule(ruleId)
    if (!token || !accountType || !(isCug || isB2cMetasearchRateRule(ruleId))) return undefined

    const metasearchPropertyId = matchLocaleAwarePath<{ propertyId?: string }>(pathname, METASEARCH_PAGE_PATH)?.params.propertyId
    const [roomId, refundable, propertyId] = metasearchPropertyId
      ? [stringParam(query?.roomTypeId), parseGhaPackageRefundable(stringParam(query?.roomRateId)), metasearchPropertyId]
      : [stringParam(query?.ghaRoomId), parseGhaRefundableParam(stringParam(query?.ghaRefundable)), stringParam(query?.ghaPropertyId)]
    if (!roomId || refundable === undefined || !propertyId) return undefined

    const googlePrice = parseAmount(query?.[queryKeySourceTotal])
    const googleCurrency = stringParam(metasearchPropertyId ? query?.currency : query?.ghaCurrency)
    return { token, roomId, refundable, isCug, propertyId, accountType, googlePrice, googleCurrency }
  },
)

/**
 * The click-through to send with this property's rate requests — `ghaPricedRequest` pairs it with
 * the request's source. Undefined for any other property, and until svc-connection-google
 * has said yes.
 */
export const getGhaPriceMatchForProperty = createSelector(
  (state: App.State) => getGhaPriceMatch(state),
  (state: App.State) => state.marketing.ghaPriceMatchEligibility,
  (state: App.State) => state.offer.propertyOfferMappings,
  (state: App.State) => state.marketing.metasearchPublicRateMarkedAt,
  (_state: App.State, propertyId: string) => propertyId,
  (clickThrough, eligibilityByKey, mappings, publicRateMarkedAt, propertyId): GhaPriceMatchClickThrough | undefined => {
    // GHA-404: a flagged shopper gets no price match either; the session prices at the public rate.
    if (isMetasearchPublicRateActive(publicRateMarkedAt)) return undefined
    if (!clickThrough) return undefined
    const eligibility = eligibilityByKey[ghaPriceMatchKey(clickThrough)]
    if (!eligibility?.priceMatch) return undefined
    // The single-property page prices by the accommodation property id; its mapping names the
    // bedbank offer Google advertised.
    const mappedPropertyId = mappings[clickThrough.propertyId]?.mapping?.propertyId
    return propertyId === clickThrough.propertyId || propertyId === mappedPropertyId ?
        { ...clickThrough, boardCode: eligibility.boardCode } :
      undefined
  },
  { memoize: weakMapMemoize, argsMemoize: weakMapMemoize },
)
